import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { listEdges, type EdgeListFilters } from '../api/edges'
import type { BlockEdge } from '@arkturian/canvas-engine'

interface UseEdgesOptions {
  postId: number | null
  filters?: EdgeListFilters
  enabled?: boolean
}

/**
 * Subscribe a component to the block_edges of a post. Backed by React
 * Query. Unlike useFieldMap (which polls), this hook refetches **only
 * on demand** plus when a chunk_committed event signals the doc has
 * changed — see useEdgesInvalidateOnCommit below.
 *
 * No periodic polling: edges change only when a paragraph is written
 * (then auto-detection may create new edges) or when an author
 * explicitly POSTs an edge. Both are observable via chunk_committed
 * or via the explicit create call's response.
 */
export function useEdges({ postId, filters, enabled = true }: UseEdgesOptions) {
  return useQuery<BlockEdge[]>({
    queryKey: ['edges', postId, filters],
    queryFn: () => listEdges(postId!, filters ?? {}),
    enabled: enabled && postId !== null,
    staleTime: 30_000,
  })
}

/**
 * Invalidate the edges cache for a post whenever a chunk_committed
 * push arrives. Called by CanvasView on mount; the push handler in
 * CollabEditor (Y-Websocket awareness) already detects commits — we
 * just need to wire that to React Query.
 *
 * Skeleton implementation: invalidate on every doc tick. A finer hook
 * would invalidate only when the push payload's `edges_auto_created`
 * is positive, but that's a Phase-5a-polish optimization.
 */
export function useEdgesInvalidateOnCommit(
  postId: number | null,
  docVersion: number | undefined,
) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (postId == null || docVersion === undefined) return
    queryClient.invalidateQueries({ queryKey: ['edges', postId] })
  }, [postId, docVersion, queryClient])
}
